import React from 'react';
import PropTypes from 'prop-types';
import { LuChartLine, LuInfo } from 'react-icons/lu';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Button from '../../../components/ui/Button';
import useSmoText from '../smoText';
import { ChartCard, useChartName, useChartTheme } from './chartKit';
import { TEXT_TONES } from './tones';

const CELL = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-tabular-nums';
const shortName = (name) => (name.length > 18 ? `${name.substring(0, 17)}…` : name);

/** Body of "Ekip Performans Analizi" (Section 3). */
const TeamPerformance = ({ employeeStats, visits, openIssueModal, onOpenStats }) => {
  const { label } = useSmoText();
  const chart = useChartTheme();
  const chartName = useChartName();
  const detailHint = label('common.detailHint');

  return (
    <div className="tw-space-y-4">
      <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
        <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-sky-500" />
        {label('performance.help')}
      </p>

      {/* ── Simplified Employee Table ── */}
      <div className="tw-relative tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white glass-scrollbar dark:tw-border-white/10 dark:tw-bg-[#1f232b]">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
            <tr>
              {['coordinator', 'visits', 'sla', 'ecrf', 'source', 'issueDeviation', 'km', 'overdue', 'actions'].map((key, i) => (
                <th key={key} scope="col" className={`tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400 ${i === 0 ? 'tw-sticky tw-left-0 tw-bg-slate-50 tw-text-left dark:tw-bg-[#262a32]' : 'tw-text-left'}`}>
                  {label(`performance.columns.${key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
            {[...employeeStats.stats].sort((a, b) => b.totalVisits - a.totalVisits).map((emp) => {
              const empVisits = visits.filter((v) => v.employeeId === emp.id);
              const empIssueCount = empVisits.filter((v) => v.visitEvaluation?.issueOccurred === 'yes').length;
              const issueOrDeviation = empVisits.filter((v) => v.visitEvaluation?.issueOccurred === 'yes' || v.visitEvaluation?.protocolDeviation === 'yes');
              const issueTone = (emp.deviationCount + empIssueCount) > 0 ? TEXT_TONES.warn : TEXT_TONES.ok;
              return (
                <tr key={emp.id}>
                  <td className="tw-sticky tw-left-0 tw-whitespace-nowrap tw-bg-white tw-px-3 tw-py-2.5 tw-font-semibold tw-text-slate-900 dark:tw-bg-[#1f232b] dark:tw-text-white">{emp.name}</td>
                  <td className={CELL}>{emp.totalVisits} <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">({emp.homeVisits}/{emp.siteVisits})</span></td>
                  <td className={`${CELL} tw-font-semibold ${emp.slaMetPct < 80 ? TEXT_TONES.bad : TEXT_TONES.ok}`}>{emp.slaMetPct}%</td>
                  <td className={CELL}>{emp.ecrfPct}%</td>
                  <td className={CELL}>{emp.sourcePct}%</td>
                  <td className={CELL}>
                    {issueOrDeviation.length > 0 ? (
                      <button type="button" onClick={() => openIssueModal(label('issue.personIssueDeviation', { name: emp.name }), issueOrDeviation)} title={detailHint} className={`tw-rounded-lg tw-bg-transparent tw-px-2 tw-py-0.5 tw-underline tw-decoration-dotted tw-underline-offset-4 hover:tw-bg-slate-900/[0.05] dark:hover:tw-bg-white/[0.06] ${issueTone}`}>
                        {empIssueCount} / {emp.deviationCount}
                      </button>
                    ) : <span className={issueTone}>{empIssueCount} / {emp.deviationCount}</span>}
                  </td>
                  <td className={CELL}>{emp.totalKm}</td>
                  <td className={CELL}>
                    {emp.overdueCount > 0 ? (
                      <button type="button" onClick={() => openIssueModal(label('issue.personOverdue', { name: emp.name }), empVisits, 'overdue')} title={detailHint} className={`tw-rounded-lg tw-bg-transparent tw-px-2 tw-py-0.5 tw-font-bold tw-underline tw-decoration-dotted tw-underline-offset-4 hover:tw-bg-slate-900/[0.05] dark:hover:tw-bg-white/[0.06] ${TEXT_TONES.bad}`}>
                        {emp.overdueCount}
                      </button>
                    ) : emp.overdueCount}
                  </td>
                  <td className={CELL}>
                    <Button size="sm" onClick={() => onOpenStats(emp)} title={label('performance.statsHint')}>
                      <LuChartLine aria-hidden="true" className="tw-h-4 tw-w-4" />
                      {label('performance.stats')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Charts ── */}
      <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2 2xl:tw-grid-cols-3">
        {/* Workload Distribution: Stacked Home/Site visits per employee */}
        {employeeStats.stats.length > 0 && (
          <ChartCard title={label('performance.workload')}>
            <ResponsiveContainer width="100%" height={Math.max(280, employeeStats.stats.length * 36)}>
              <BarChart
                data={[...employeeStats.stats].sort((a, b) => b.totalVisits - a.totalVisits).slice(0, 10).map((s) => ({
                  name: shortName(s.name),
                  fullName: s.name,
                  'Ev Viziti': s.homeVisits,
                  'Hastane Viziti': s.siteVisits,
                }))}
                layout="vertical"
                margin={{ left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis type="number" tick={chart.tick} />
                <YAxis dataKey="name" type="category" tick={chart.tick} width={120} />
                <Tooltip labelFormatter={(l) => { const d = employeeStats.stats.find((x) => shortName(x.name) === l); return d?.name || l; }} {...chart.tooltip} />
                <Legend wrapperStyle={chart.legend} />
                <Bar dataKey="Ev Viziti" name={chartName('Ev Viziti')} stackId="visits" fill="#4299e1" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Hastane Viziti" name={chartName('Hastane Viziti')} stackId="visits" fill="#9f7aea" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* KM per Employee */}
        {employeeStats.kmPerNurse.length > 0 && (
          <ChartCard title={label('performance.kmPerEmployee')}>
            <ResponsiveContainer width="100%" height={Math.max(280, employeeStats.kmPerNurse.length * 36)}>
              <BarChart data={employeeStats.kmPerNurse} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis type="number" tick={chart.tick} />
                <YAxis dataKey="name" type="category" tick={chart.tick} width={120} />
                <Tooltip labelFormatter={(l) => { const d = employeeStats.kmPerNurse.find((x) => x.name === l); return d?.fullName || l; }} {...chart.tooltip} />
                <Bar dataKey="KM" name="KM" radius={[0, 4, 4, 0]}>
                  {employeeStats.kmPerNurse.map((entry, i) => {
                    const colors = ['#4299e1', '#fc8181', '#68d391', '#9f7aea', '#4fd1c5', '#f6ad55', '#4299e1', '#fc8181', '#68d391', '#9f7aea'];
                    return <Cell key={entry.fullName} fill={colors[i % colors.length]} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* SLA: Home vs Site */}
        <ChartCard title={label('performance.slaHomeSite')}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={employeeStats.homeVsSiteSla}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="name" tick={chart.tick} tickFormatter={chartName} />
              <YAxis unit="%" domain={[0, 100]} tick={chart.tick} />
              <Tooltip formatter={(v) => `${v}%`} labelFormatter={chartName} {...chart.tooltip} />
              <Legend wrapperStyle={chart.legend} />
              <Bar dataKey="SLA Karşılanma %" name={chartName('SLA Karşılanma %')} fill="#4299e1" radius={[4, 4, 0, 0]}>
                {employeeStats.homeVsSiteSla.map((entry, i) => (
                  <Cell key={entry.name} fill={i === 0 ? '#4299e1' : '#9f7aea'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

TeamPerformance.propTypes = {
  employeeStats: PropTypes.object.isRequired,
  visits: PropTypes.array.isRequired,
  openIssueModal: PropTypes.func.isRequired,
  onOpenStats: PropTypes.func.isRequired,
};

export default TeamPerformance;
